import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Store, Package, Eye, CheckCircle2, Plus, Sparkles, Upload, X, FileSpreadsheet } from 'lucide-react';
import { useLocale } from '@/contexts/LocaleContext';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingFlow = ({ onComplete }) => {
  const navigate = useNavigate();
  const { isUSA } = useLocale();
  const { user, token } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [storeCreated, setStoreCreated] = useState(false);
  const [storeData, setStoreData] = useState(null);
  
  // Store form
  const [storeName, setStoreName] = useState('');
  const [storeType, setStoreType] = useState('');
  
  // Products
  const [products, setProducts] = useState([{ name: '', price: '', image: null, imagePreview: null }]);

  const totalSteps = 6;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const storeTypes = isUSA ? [
    { value: 'telas', label: 'Fabrics' },
    { value: 'ropa', label: 'Clothing' },
    { value: 'accesorios', label: 'Trims & Accessories' }
  ] : [
    { value: 'telas', label: 'Telas' },
    { value: 'ropa', label: 'Ropa' },
    { value: 'accesorios', label: 'Avíos y Accesorios' }
  ];

  // Check if user already has a store
  useEffect(() => {
    const checkStore = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/api/stores/my`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data && data.store_id) {
            setStoreCreated(true);
            setStoreData(data);
            // Get product count (default to 0 if not available)
            const productCount = data.products_count || data.product_count || 0;
            // Skip to step 2 (add products) if store exists but has few products
            if (productCount < 3) {
              setCurrentStep(2);
            } else {
              setCurrentStep(3); // Go to preview
            }
          }
        }
      } catch (e) {
        console.error('Error checking store:', e);
      }
    };
    checkStore();
  }, [token]);

  const handleCreateStore = async () => {
    if (!storeName.trim() || !storeType) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/stores`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          store_name: storeName,
          description: `${storeName} - ${storeType}`,
          category: storeType,
          accepted_terms: true
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setStoreCreated(true);
        setStoreData(data);
        setCurrentStep(2);
      } else if (res.status === 403) {
        // User needs Pro plan - redirect silently to subscription page
        navigate('/dashboard/subscription');
        return;
      } else {
        const error = await res.json();
        if (error.detail?.includes('empresa')) {
          // User needs to create a company first - redirect silently
          navigate('/dashboard/company/new');
        } else {
          // Only show alert for unexpected errors
          alert(error.detail || (isUSA ? 'Error creating store' : 'Error al crear la tienda'));
        }
      }
    } catch (e) {
      console.error('Error creating store:', e);
      alert(isUSA ? 'Connection error. Please try again.' : 'Error de conexión. Por favor intenta de nuevo.');
    }
    setLoading(false);
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const newProducts = [...products];
      newProducts[index].imagePreview = e.target.result;
      newProducts[index].image = file;
      setProducts(newProducts);
    };
    reader.readAsDataURL(file);
  };

  const addProduct = () => {
    if (products.length < 5) {
      setProducts([...products, { name: '', price: '', image: null, imagePreview: null }]);
    }
  };

  const removeProduct = (index) => {
    if (products.length > 1) {
      setProducts(products.filter((_, i) => i !== index));
    }
  };

  const handleSaveProducts = async () => {
    const validProducts = products.filter(p => p.name.trim());
    if (validProducts.length === 0) return;
    
    setLoading(true);
    try {
      for (const product of validProducts) {
        let imageUrls = [];
        
        // Upload image if exists
        if (product.image) {
          const formData = new FormData();
          formData.append('file', product.image);
          
          const uploadRes = await fetch(`${API_URL}/api/upload/image`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          });
          
          if (uploadRes.ok) {
            const uploadData = await uploadRes.json();
            imageUrls = [uploadData.url];
          }
        }
        
        // Create product
        await fetch(`${API_URL}/api/stores/my/products`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: product.name,
            category: storeType || 'telas',
            description: product.name,
            price: product.price ? parseFloat(product.price) : null,
            images: imageUrls,
            publish_region: isUSA ? 'US' : 'MX'
          })
        });
      }
      
      setCurrentStep(3);
    } catch (e) {
      console.error('Error saving products:', e);
    }
    setLoading(false);
  };

  const handleViewStore = () => {
    if (storeData?.slug) {
      window.open(`/tienda/${storeData.slug}`, '_blank');
    } else if (storeData?.store_id) {
      window.open(`/tienda/${storeData.store_id}`, '_blank');
    }
    // Redirect to subscription page to complete payment
    navigate('/dashboard/subscription');
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete();
    } else {
      navigate('/dashboard');
    }
  };

  const handleActivatePro = () => {
    navigate('/dashboard/subscription');
  };

  // Step 0: Welcome
  const renderWelcome = () => (
    <div className="text-center py-8">
      <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <Store className="w-10 h-10 text-emerald-600" />
      </div>
      <h1 className="text-2xl font-bold text-slate-900 mb-3">
        {isUSA ? 'Activate your store in 3 steps' : 'Activa tu tienda en 3 pasos'}
      </h1>
      <p className="text-slate-600 mb-8">
        {isUSA 
          ? 'In just a few minutes, your products will be visible to thousands of buyers'
          : 'En solo unos minutos, tus productos estarán visibles para miles de compradores'}
      </p>
      <Button 
        className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-8 py-6 text-lg"
        onClick={() => setCurrentStep(1)}
        data-testid="onboarding-start-btn"
      >
        {isUSA ? 'Get Started' : 'Empezar'}
      </Button>
    </div>
  );

  // Step 1: Create Store
  const renderCreateStore = () => (
    <div className="py-6">
      <h2 className="text-xl font-bold text-slate-900 mb-6 text-center">
        {isUSA ? 'Create your store' : 'Crea tu tienda'}
      </h2>
      
      <div className="space-y-4">
        <div>
          <Label>{isUSA ? 'Business name' : 'Nombre del negocio'}</Label>
          <Input 
            value={storeName}
            onChange={(e) => setStoreName(e.target.value)}
            placeholder={isUSA ? 'My Textile Store' : 'Mi Tienda Textil'}
            className="mt-1"
          />
        </div>
        
        <div>
          <Label>{isUSA ? 'Type' : 'Tipo'}</Label>
          <Select value={storeType} onValueChange={setStoreType}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder={isUSA ? 'Select type' : 'Selecciona tipo'} />
            </SelectTrigger>
            <SelectContent>
              {storeTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <Button 
        className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold mt-6 py-5"
        onClick={handleCreateStore}
        disabled={!storeName.trim() || !storeType || loading}
      >
        {loading ? (isUSA ? 'Creating...' : 'Creando...') : (isUSA ? 'Create store' : 'Crear tienda')}
      </Button>
    </div>
  );

  // Step 2: Add Products
  const renderAddProducts = () => (
    <div className="py-6">
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <CheckCircle2 className="w-6 h-6 text-emerald-600" />
        </div>
        <h2 className="text-xl font-bold text-slate-900">
          {isUSA ? 'Your store is ready!' : '¡Tu tienda está lista!'}
        </h2>
        <p className="text-slate-600 text-sm mt-1">
          {isUSA ? 'Now add your first products' : 'Ahora sube tu primer producto'}
        </p>
      </div>
      
      <div className="space-y-4 max-h-[400px] overflow-y-auto">
        {products.map((product, index) => (
          <Card key={index} className="p-4 relative">
            {products.length > 1 && (
              <button 
                onClick={() => removeProduct(index)}
                className="absolute top-2 right-2 text-slate-400 hover:text-red-500"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            
            <div className="flex gap-4">
              {/* Image Upload */}
              <div className="flex-shrink-0">
                <label className="block w-20 h-20 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-emerald-500 overflow-hidden">
                  {product.imagePreview ? (
                    <img src={product.imagePreview} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400">
                      <Upload className="w-5 h-5" />
                      <span className="text-xs mt-1">{isUSA ? 'Photo' : 'Foto'}</span>
                    </div>
                  )}
                  <input 
                    type="file" 
                    accept="image/*" 
                    className="hidden"
                    onChange={(e) => handleImageUpload(index, e.target.files[0])}
                  />
                </label>
              </div>
              
              {/* Product Details */}
              <div className="flex-1 space-y-2">
                <Input 
                  value={product.name}
                  onChange={(e) => {
                    const newProducts = [...products];
                    newProducts[index].name = e.target.value;
                    setProducts(newProducts);
                  }}
                  placeholder={isUSA ? 'Product name' : 'Nombre del producto'}
                  className="text-sm"
                />
                <Input 
                  type="number"
                  value={product.price}
                  onChange={(e) => {
                    const newProducts = [...products];
                    newProducts[index].price = e.target.value;
                    setProducts(newProducts);
                  }}
                  placeholder={isUSA ? 'Price (optional)' : 'Precio (opcional)'}
                  className="text-sm"
                />
              </div>
            </div>
          </Card>
        ))}
      </div>
      
      {products.length < 5 && (
        <Button 
          variant="outline" 
          className="w-full mt-4"
          onClick={addProduct}
        >
          <Plus className="w-4 h-4 mr-2" />
          {isUSA ? 'Add another product' : 'Duplicar producto'}
        </Button>
      )}
      
      {/* Excel Import Tip */}
      <div className="mt-4 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
        <p className="text-xs text-indigo-700 flex items-center gap-2">
          <Sparkles className="w-4 h-4 flex-shrink-0" />
          <span>
            {isUSA 
              ? '💡 Have many products? Import them via Excel! '
              : '💡 ¿Tienes muchos productos? ¡Impórtalos por Excel! '}
            <button 
              onClick={() => navigate('/help/excel')} 
              className="underline font-medium hover:text-indigo-900"
            >
              {isUSA ? 'See tutorial' : 'Ver tutorial'}
            </button>
          </span>
        </p>
      </div>
      
      <p className="text-xs text-slate-500 text-center mt-3">
        {isUSA 
          ? `Add at least 3 products for better visibility (${products.filter(p => p.name.trim()).length}/3)`
          : `Agrega al menos 3 productos para mejor visibilidad (${products.filter(p => p.name.trim()).length}/3)`}
      </p>
      
      <Button 
        className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold mt-4 py-5"
        onClick={handleSaveProducts}
        disabled={!products.some(p => p.name.trim()) || loading}
      >
        {loading ? (isUSA ? 'Saving...' : 'Guardando...') : (isUSA ? 'Continue' : 'Continuar')}
      </Button>
    </div>
  );

  // Step 3: Preview (WOW Moment)
  const renderPreview = () => {
    // Use actual store data if available, otherwise use form data
    const displayName = storeData?.store_name || storeName || (isUSA ? 'Your Store' : 'Tu Tienda');
    const productCount = storeData?.products_count || products.filter(p => p.name).length;
    
    return (
      <div className="py-6 text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <Eye className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-2">
          {isUSA ? 'This is how customers will see your store' : 'Así verán tu tienda los clientes'}
        </h2>
        <p className="text-slate-600 text-sm mb-6">
          {isUSA ? 'Your products are now visible to buyers' : 'Tus productos ya están visibles para compradores'}
        </p>
        
        {/* Mini Preview Card */}
        <Card className="p-4 mb-6 bg-slate-50">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold">
              {displayName.charAt(0)}
            </div>
            <div className="text-left">
              <p className="font-semibold text-slate-900">{displayName}</p>
              <p className="text-xs text-slate-500">{productCount} {isUSA ? 'products' : 'productos'}</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {products.slice(0, 3).map((p, i) => (
              <div key={i} className="aspect-square bg-slate-200 rounded-lg overflow-hidden">
                {p.imagePreview ? (
                  <img src={p.imagePreview} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400">
                    <Package className="w-6 h-6" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
        
        <Button 
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-5"
          onClick={handleViewStore}
        >
          {isUSA ? 'View my store' : 'Ver mi tienda'}
        </Button>
      </div>
    );
  };

  // Step 4: Activation Message
  const renderActivation = () => (
    <div className="py-6 text-center">
      <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircle2 className="w-10 h-10 text-white" />
      </div>
      <h2 className="text-2xl font-bold text-slate-900 mb-3">
        {isUSA ? 'Your store is now active!' : '¡Tu tienda ya está activa!'}
      </h2>
      <p className="text-slate-600 mb-8">
        {isUSA 
          ? 'Ready to receive inquiries from buyers'
          : 'Lista para recibir contactos de compradores'}
      </p>
      <Button 
        className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-5"
        onClick={() => setCurrentStep(5)}
      >
        {isUSA ? 'Continue' : 'Continuar'}
      </Button>
    </div>
  );

  // Step 5: Upgrade Prompt
  const renderUpgrade = () => (
    <div className="py-6 text-center">
      <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
        <Sparkles className="w-8 h-8 text-white" />
      </div>
      <h2 className="text-xl font-bold text-slate-900 mb-2">
        {isUSA ? 'Start receiving quote requests' : 'Empieza a recibir cotizaciones'}
      </h2>
      <p className="text-slate-600 text-sm mb-6">
        {isUSA 
          ? 'Upgrade to PRO to unlock all features'
          : 'Mejora a PRO para desbloquear todas las funciones'}
      </p>
      
      <Card className="p-4 mb-6 border-2 border-emerald-500 bg-emerald-50">
        <p className="text-emerald-700 font-semibold text-sm">
          {isUSA ? '✨ Unlimited catalog & TexVision AI' : '✨ Catálogo ilimitado y TexVision AI'}
        </p>
        <p className="text-emerald-600 text-xs mt-1">
          {isUSA ? '$49 USD/month - Cancel anytime' : '$599 MXN/mes - Cancela cuando quieras'}
        </p>
      </Card>
      
      <Button 
        className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-5 mb-3"
        onClick={handleActivatePro}
      >
        {isUSA ? 'Activate TexHub Pro' : 'Activar TexHub Pro'}
      </Button>
      
      <Button 
        variant="ghost"
        className="w-full text-slate-500"
        onClick={handleComplete}
      >
        {isUSA ? 'Maybe later' : 'Quizás después'}
      </Button>
    </div>
  );

  const steps = [
    renderWelcome,
    renderCreateStore,
    renderAddProducts,
    renderPreview,
    renderActivation,
    renderUpgrade
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        {/* Progress Bar */}
        {currentStep > 0 && (
          <div className="p-4 border-b">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-500">
                {isUSA ? `Step ${currentStep} of ${totalSteps - 1}` : `Paso ${currentStep} de ${totalSteps - 1}`}
              </span>
              <span className="text-xs font-medium text-emerald-600">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}
        
        <CardContent className="p-6">
          {steps[currentStep]()}
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingFlow;
